import { Controller } from "@hotwired/stimulus";

// Selector de icono o imagen para items tipo "Tarjeta" del editor de paginas.
// Replica WebsiteEditorSectionItemFormIcon + Modal + Icons + Images (front React).
//
// Trabaja en conjunto con manage--modal (registrado en el mismo elemento).
//
// Maneja:
//   - Switch entre tabs Iconos/Imagen.
//   - Seleccion de un icono FA (escribe hidden inputs + cierra modal + auto-submit).
//   - Upload AJAX de imagen a media_uploads_create_pending (recibe original_url,
//     escribe hidden inputs + cierra modal + auto-submit).
//   - Eliminar icono actual (limpia inputs + auto-submit).
//
// El form padre (edit.html.erb) tiene manage--form-auto-submit con
// change->submitNow — dispatchear `change` sobre un hidden input
// dispara el auto-guardado.
export default class extends Controller {
  static targets = [
    "preview",
    "imagePreview",
    "imagePreviewImg",
    "iconHolder",
    "toggleLabel",
    "removeWrap",
    "iconInput",
    "customTypeInput",
    "customIconInput",
    "customImageInput",
    "iconsTabBtn",
    "imageTabBtn",
    "iconsPanel",
    "imagePanel",
    "fileInput",
    "uploadThumb",
    "uploadPreviewImg",
    "uploadBtnIconHolder",
    "uploadBtnLabel",
    "uploadSpinner",
  ];

  static values = {
    uploadUrl: String,
    labelAdd: String,
    labelChange: String,
    labelUpload: String,
    labelReplace: String,
    alertNotImage: String,
  };

  // El modal hace _restoreOriginalContent al cerrar — esto reclona los hijos
  // del panel al estado del server render. Cuando el panel se restaura,
  // los targets internos se reconectan y disparamos sync para reflejar el
  // estado actual (leido de los hidden inputs que viven fuera del panel).
  iconsPanelTargetConnected() {
    this._syncModalState();
  }

  imagePanelTargetConnected() {
    this._syncModalState();
  }

  // === Tabs ===

  showIconsTab(event) {
    event.preventDefault();
    event.stopPropagation();
    this._toggleTab("icons");
  }

  showImageTab(event) {
    event.preventDefault();
    event.stopPropagation();
    this._toggleTab("image");
  }

  // === Seleccion de icono FA ===

  selectIcon(event) {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget;
    const name = button.dataset.iconName;
    if (!name) return;

    this._setIconState({
      icon: name,
      customType: "icon",
      customIcon: name,
      customImage: "",
    });

    this._renderPreviewIcon(name);
    this._closeModal();
    this._notifyChange();
  }

  // === Upload de imagen ===

  async uploadImage(event) {
    const input = event.currentTarget;
    const file = input.files && input.files[0];
    if (!file) return;

    if (!file.type || !file.type.includes("image")) {
      window.alert(this.alertNotImageValue || "El archivo no es un formato de imagen");
      input.value = "";
      return;
    }

    this._setUploading(true);

    try {
      const url = await this._uploadFile(file);
      if (!url) {
        window.alert("Error al subir la imagen");
        return;
      }

      this._setIconState({
        icon: "",
        customType: "image",
        customIcon: "",
        customImage: url,
      });

      this._renderPreviewImage(url);
      this._closeModal();
      this._notifyChange();
    } catch (e) {
      console.error(e);
      window.alert(e.message || "Error al subir la imagen");
    } finally {
      this._setUploading(false);
      input.value = "";
    }
  }

  // === Eliminar icono ===

  remove(event) {
    event.preventDefault();

    this._setIconState({
      icon: "",
      customType: "",
      customIcon: "",
      customImage: "",
    });

    this._hidePreview();
    this._notifyChange();
  }

  // === Private ===

  // Aplica el state actual (leido de hidden inputs) al panel del modal:
  // tab activo, thumbnail del upload, label/icono del boton subir/replazar.
  // Se invoca cuando los targets internos se conectan (incl. re-clone tras
  // _restoreOriginalContent del modal controller).
  _syncModalState() {
    const type = this.hasCustomTypeInputTarget ? this.customTypeInputTarget.value : "";
    const image = this.hasCustomImageInputTarget ? this.customImageInputTarget.value : "";
    const tab = type === "image" ? "image" : "icons";

    this._toggleTab(tab);

    // Sync upload thumbnail dentro del modal con la imagen actual
    if (this.hasUploadThumbTarget) {
      this.uploadThumbTarget.classList.toggle("hidden", !image);
    }
    if (this.hasUploadPreviewImgTarget && image) {
      this.uploadPreviewImgTarget.src = image;
    }
    if (image) {
      this._setUploadBtnReplace();
    } else {
      this._setUploadBtnUpload();
    }
  }

  _toggleTab(tab) {
    if (this.hasIconsTabBtnTarget && this.hasImageTabBtnTarget) {
      this._setTabActive(this.iconsTabBtnTarget, tab === "icons");
      this._setTabActive(this.imageTabBtnTarget, tab === "image");
    }
    if (this.hasIconsPanelTarget) this.iconsPanelTarget.classList.toggle("hidden", tab !== "icons");
    if (this.hasImagePanelTarget) this.imagePanelTarget.classList.toggle("hidden", tab !== "image");
  }

  // Cambia el theme del btn_to entre primary y default sin re-renderizar el
  // boton: las clases de btn_to incluyen `bg-[#hex] text-[#hex]` — las
  // intercambiamos manualmente para mantener paridad con btn_to.
  _setTabActive(btn, active) {
    if (active) {
      btn.classList.remove("bg-[#f2f2f2]", "text-[#000000]");
      btn.classList.add("bg-[#000000]", "text-[#FFFFFF]");
    } else {
      btn.classList.remove("bg-[#000000]", "text-[#FFFFFF]");
      btn.classList.add("bg-[#f2f2f2]", "text-[#000000]");
    }
  }

  _setIconState({ icon, customType, customIcon, customImage }) {
    if (this.hasIconInputTarget) this.iconInputTarget.value = icon || "";
    if (this.hasCustomTypeInputTarget) this.customTypeInputTarget.value = customType || "";
    if (this.hasCustomIconInputTarget) this.customIconInputTarget.value = customIcon || "";
    if (this.hasCustomImageInputTarget) this.customImageInputTarget.value = customImage || "";
  }

  _renderPreviewIcon(name) {
    if (this.hasPreviewTarget) this.previewTarget.classList.remove("hidden");
    if (this.hasImagePreviewTarget) this.imagePreviewTarget.classList.add("hidden");
    if (this.hasIconHolderTarget) {
      // Reemplazar contenido del slot por un nuevo <i>: dom.watch() lo procesa
      // y lo convierte en <svg> con el path correcto. Mutar clases del <svg>
      // ya generado NO actualiza el icono (el SVG es estatico).
      this.iconHolderTarget.replaceChildren(this._buildIcon(`fa-light fa-${name}`));
      this.iconHolderTarget.classList.remove("hidden");
    }
    this._setToggleLabelToChange();
    this._showRemoveButton();
  }

  _renderPreviewImage(url) {
    if (this.hasPreviewTarget) this.previewTarget.classList.remove("hidden");
    if (this.hasIconHolderTarget) this.iconHolderTarget.classList.add("hidden");
    if (this.hasImagePreviewTarget) this.imagePreviewTarget.classList.remove("hidden");
    if (this.hasImagePreviewImgTarget) this.imagePreviewImgTarget.src = url;

    // Tambien actualiza el thumb del UploadCard dentro del modal
    if (this.hasUploadThumbTarget) this.uploadThumbTarget.classList.remove("hidden");
    if (this.hasUploadPreviewImgTarget) this.uploadPreviewImgTarget.src = url;
    this._setUploadBtnReplace();
    this._setToggleLabelToChange();
    this._showRemoveButton();
  }

  _hidePreview() {
    if (this.hasPreviewTarget) this.previewTarget.classList.add("hidden");
    if (this.hasImagePreviewTarget) this.imagePreviewTarget.classList.add("hidden");
    if (this.hasIconHolderTarget) {
      this.iconHolderTarget.replaceChildren();
      this.iconHolderTarget.classList.add("hidden");
    }
    if (this.hasUploadThumbTarget) this.uploadThumbTarget.classList.add("hidden");
    this._setUploadBtnUpload();
    this._setToggleLabelToAdd();
    this._hideRemoveButton();
  }

  // Crea un <i class="..."> nuevo. dom.watch() (FA SVG core) lo detecta via
  // MutationObserver y lo reemplaza con el <svg> correcto.
  _buildIcon(className) {
    const el = document.createElement("i");
    el.className = className;
    return el;
  }

  _setToggleLabelToAdd() {
    if (this.hasToggleLabelTarget && this.hasLabelAddValue) {
      this.toggleLabelTarget.textContent = this.labelAddValue;
    }
  }

  _setToggleLabelToChange() {
    if (this.hasToggleLabelTarget && this.hasLabelChangeValue) {
      this.toggleLabelTarget.textContent = this.labelChangeValue;
    }
  }

  _showRemoveButton() {
    if (this.hasRemoveWrapTarget) this.removeWrapTarget.classList.remove("hidden");
  }

  _hideRemoveButton() {
    if (this.hasRemoveWrapTarget) this.removeWrapTarget.classList.add("hidden");
  }

  _setUploadBtnReplace() {
    if (this.hasUploadBtnIconHolderTarget) {
      this.uploadBtnIconHolderTarget.replaceChildren(this._buildIcon("fa-regular fa-rotate"));
    }
    if (this.hasUploadBtnLabelTarget && this.hasLabelReplaceValue) {
      this.uploadBtnLabelTarget.textContent = this.labelReplaceValue;
    }
  }

  _setUploadBtnUpload() {
    if (this.hasUploadBtnIconHolderTarget) {
      this.uploadBtnIconHolderTarget.replaceChildren(this._buildIcon("fa-regular fa-upload"));
    }
    if (this.hasUploadBtnLabelTarget && this.hasLabelUploadValue) {
      this.uploadBtnLabelTarget.textContent = this.labelUploadValue;
    }
  }

  _setUploading(loading) {
    if (this.hasUploadSpinnerTarget) {
      this.uploadSpinnerTarget.classList.toggle("hidden", !loading);
    }
    if (this.hasFileInputTarget) {
      this.fileInputTarget.disabled = loading;
    }
  }

  // Dispatch change sobre el hidden iconInput para que
  // manage--form-auto-submit del padre dispare submitNow.
  _notifyChange() {
    const input = this.hasIconInputTarget
      ? this.iconInputTarget
      : this.element.querySelector("input[type=hidden]");
    if (!input) return;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  _closeModal() {
    // El controller manage--modal vive en el mismo elemento — llamamos close
    // via el API del MutationObserver de Stimulus.
    const modalCtrl = this.application.getControllerForElementAndIdentifier(
      this.element,
      "manage--modal"
    );
    if (modalCtrl && typeof modalCtrl.close === "function") {
      modalCtrl.close();
    }
  }

  async _uploadFile(file) {
    if (!this.uploadUrlValue) {
      throw new Error("Upload URL no configurada");
    }
    const data = new FormData();
    data.append("file", file);

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const headers = { Accept: "application/json" };
    if (token) headers["X-CSRF-Token"] = token;

    const response = await fetch(this.uploadUrlValue, {
      method: "POST",
      body: data,
      headers,
      credentials: "same-origin",
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || `HTTP ${response.status}`);
    }
    return payload.original_url || payload.preview_url;
  }
}
